import React from "react";
import {
    MessageSquare, Mail, Send, HardDrive,
    FolderOpen, FileSpreadsheet, Clock, Users2, TrendingUp,
    Brain,
} from "lucide-react";

// Static layout (no JS animation — pure CSS/SVG, perf-friendly).
// Inspired by "Flusso di lavoro per tipografie gestito da AI".
const INPUTS = [
    { id: "wa", label: "WhatsApp", sub: "Richieste, file", icon: MessageSquare, color: "#25D366" },
    { id: "em", label: "Email", sub: "Ordini, allegati", icon: Mail, color: "#3B82F6" },
    { id: "tg", label: "Telegram", sub: "File, update", icon: Send, color: "#26A5E4" },
    { id: "dr", label: "Drive", sub: "File condivisi", icon: HardDrive, color: "#F59E0B" },
];

const OUTPUTS = [
    { id: "o1", label: "Cartelle", sub: "Auto-organizza", icon: FolderOpen, color: "#A78BFA" },
    { id: "o2", label: "Fogli lavoro", sub: "Genera schede", icon: FileSpreadsheet, color: "#34D399" },
    { id: "o3", label: "Tempi", sub: "Stima & alert", icon: Clock, color: "#FBBF24" },
    { id: "o4", label: "Reparti", sub: "Assegna task", icon: Users2, color: "#F472B6" },
    { id: "o5", label: "Tracking", sub: "Live status", icon: TrendingUp, color: "#FB923C" },
];

// SVG dimensions and node coordinates
const W = 640, H = 600;
const CX = W / 2, CY = H / 2;
const IN_X = 60, OUT_X = W - 60;
const inputY = (i, n) => 90 + (i * (H - 180)) / (n - 1);
const outputY = (i, n) => 70 + (i * (H - 160)) / (n - 1);

export default function AIFlowDiagram() {
    return (
        <div className="relative" data-testid="ai-flow-diagram">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ filter: "drop-shadow(0 20px 40px rgba(255,26,92,0.18))" }}>
                <defs>
                    <linearGradient id="brain-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FF1A5C" />
                        <stop offset="100%" stopColor="#FF6B1A" />
                    </linearGradient>
                    <radialGradient id="brain-glow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#FF1A5C" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#FF1A5C" stopOpacity="0" />
                    </radialGradient>
                    <filter id="soft-blur"><feGaussianBlur stdDeviation="6" /></filter>
                </defs>

                {/* Background pulse rings around brain */}
                <circle cx={CX} cy={CY} r="140" fill="url(#brain-glow)" />
                <circle cx={CX} cy={CY} r="72" fill="none" stroke="#FF1A5C" strokeWidth="1" strokeOpacity="0.25" className="tk-pulse-ring tk-pulse-ring-1" />
                <circle cx={CX} cy={CY} r="72" fill="none" stroke="#FF6B1A" strokeWidth="1" strokeOpacity="0.25" className="tk-pulse-ring tk-pulse-ring-2" />
                <circle cx={CX} cy={CY} r="72" fill="none" stroke="#FF1A5C" strokeWidth="1" strokeOpacity="0.25" className="tk-pulse-ring tk-pulse-ring-3" />

                {/* INPUT FLOW LINES (left → brain) */}
                {INPUTS.map((node, i) => {
                    const y1 = inputY(i, INPUTS.length);
                    const path = `M ${IN_X + 90} ${y1} C ${CX - 140} ${y1}, ${CX - 140} ${CY}, ${CX - 72} ${CY}`;
                    return (
                        <g key={`in-${i}`}>
                            <path d={path} fill="none" stroke={node.color} strokeWidth="1.5" strokeOpacity="0.35" />
                            <path d={path} fill="none" stroke={node.color} strokeWidth="2" strokeDasharray="6 10" className={`tk-flow-dash tk-flow-dash-${i}`} />
                            <circle r="3.5" fill={node.color} className={`tk-flow-dot tk-flow-dot-in-${i}`}>
                                <animateMotion dur={`${2.4 + i * 0.3}s`} repeatCount="indefinite" path={path} />
                            </circle>
                        </g>
                    );
                })}

                {/* OUTPUT FLOW LINES (brain → right) */}
                {OUTPUTS.map((node, i) => {
                    const y2 = outputY(i, OUTPUTS.length);
                    const path = `M ${CX + 72} ${CY} C ${CX + 140} ${CY}, ${CX + 140} ${y2}, ${OUT_X - 90} ${y2}`;
                    return (
                        <g key={`out-${i}`}>
                            <path d={path} fill="none" stroke={node.color} strokeWidth="1.5" strokeOpacity="0.35" />
                            <path d={path} fill="none" stroke={node.color} strokeWidth="2" strokeDasharray="6 10" className={`tk-flow-dash tk-flow-dash-o${i}`} />
                            <circle r="3.5" fill={node.color} className={`tk-flow-dot tk-flow-dot-out-${i}`}>
                                <animateMotion dur={`${2.6 + i * 0.25}s`} repeatCount="indefinite" path={path} />
                            </circle>
                        </g>
                    );
                })}

                {/* CENTER BRAIN */}
                <g>
                    <circle cx={CX} cy={CY} r="70" fill="url(#brain-grad)" />
                    <circle cx={CX} cy={CY} r="70" fill="none" stroke="#fff" strokeOpacity="0.2" strokeWidth="1" />
                    <foreignObject x={CX - 34} y={CY - 36} width="68" height="72">
                        <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                            <Brain size={32} />
                            <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.12em", marginTop: 3 }}>AI</div>
                        </div>
                    </foreignObject>
                </g>

                {/* INPUT NODES */}
                {INPUTS.map((n, i) => {
                    const y = inputY(i, INPUTS.length);
                    const Ico = n.icon;
                    return (
                        <g key={`inN-${i}`} className={`tk-node tk-node-delay-${i}`}>
                            <rect x={IN_X - 5} y={y - 26} width="180" height="52" rx="26" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.12)" />
                            <rect x={IN_X - 5} y={y - 26} width="180" height="52" rx="26" fill="none" stroke={n.color} strokeOpacity="0.5" strokeWidth="1" />
                            <foreignObject x={IN_X - 5} y={y - 26} width="180" height="52">
                                <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", gap: 12, padding: "0 14px", color: "#fff" }}>
                                    <div style={{ width: 34, height: 34, borderRadius: 17, background: n.color, display: "grid", placeItems: "center", flexShrink: 0 }}>
                                        <Ico size={17} color="#fff" />
                                    </div>
                                    <div style={{ lineHeight: 1.1 }}>
                                        <div style={{ fontSize: 14, fontWeight: 800 }}>{n.label}</div>
                                        <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>{n.sub}</div>
                                    </div>
                                </div>
                            </foreignObject>
                        </g>
                    );
                })}

                {/* OUTPUT NODES */}
                {OUTPUTS.map((n, i) => {
                    const y = outputY(i, OUTPUTS.length);
                    const Ico = n.icon;
                    return (
                        <g key={`outN-${i}`} className={`tk-node tk-node-delay-${i + 4}`}>
                            <rect x={OUT_X - 175} y={y - 26} width="180" height="52" rx="26" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.12)" />
                            <rect x={OUT_X - 175} y={y - 26} width="180" height="52" rx="26" fill="none" stroke={n.color} strokeOpacity="0.5" strokeWidth="1" />
                            <foreignObject x={OUT_X - 175} y={y - 26} width="180" height="52">
                                <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", gap: 12, padding: "0 14px", color: "#fff", justifyContent: "flex-end" }}>
                                    <div style={{ lineHeight: 1.1, textAlign: "right" }}>
                                        <div style={{ fontSize: 14, fontWeight: 800 }}>{n.label}</div>
                                        <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>{n.sub}</div>
                                    </div>
                                    <div style={{ width: 34, height: 34, borderRadius: 17, background: n.color, display: "grid", placeItems: "center", flexShrink: 0 }}>
                                        <Ico size={17} color="#fff" />
                                    </div>
                                </div>
                            </foreignObject>
                        </g>
                    );
                })}

                {/* Labels */}
                <text x={IN_X + 85} y="44" textAnchor="middle" fill="#94A3B8" fontSize="11" fontWeight="800" letterSpacing="2.5">DATI IN ENTRATA</text>
                <text x={OUT_X - 85} y="36" textAnchor="middle" fill="#94A3B8" fontSize="11" fontWeight="800" letterSpacing="2.5">L&apos;AI GESTISCE TUTTO</text>
            </svg>

            {/* Live ticker below */}
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full backdrop-blur-md flex items-center gap-2" style={{ background: "rgba(255,26,92,0.15)", border: "1px solid rgba(255,26,92,0.35)" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#10B981", boxShadow: "0 0 8px #10B981" }} />
                <span className="text-[10px] mono uppercase tracking-[0.15em] font-extrabold text-white">Live · 142 ordini elaborati oggi</span>
            </div>

            <style>{`
                .tk-flow-dash {
                    animation: tk-dash-flow 1.8s linear infinite;
                    stroke-opacity: 0.85;
                }
                @keyframes tk-dash-flow {
                    to { stroke-dashoffset: -32; }
                }
                .tk-flow-dash-1 { animation-duration: 2.1s; }
                .tk-flow-dash-2 { animation-duration: 1.6s; }
                .tk-flow-dash-3 { animation-duration: 2.3s; }
                .tk-flow-dash-o0 { animation-duration: 1.9s; }
                .tk-flow-dash-o1 { animation-duration: 2.2s; }
                .tk-flow-dash-o2 { animation-duration: 1.7s; }
                .tk-flow-dash-o3 { animation-duration: 2.4s; }
                .tk-flow-dash-o4 { animation-duration: 1.85s; }

                .tk-pulse-ring {
                    transform-origin: ${CX}px ${CY}px;
                    animation: tk-pulse 2.4s ease-out infinite;
                }
                .tk-pulse-ring-2 { animation-delay: 0.8s; }
                .tk-pulse-ring-3 { animation-delay: 1.6s; }
                @keyframes tk-pulse {
                    0% { transform: scale(0.7); opacity: 0.6; }
                    100% { transform: scale(2.6); opacity: 0; }
                }

                .tk-node { animation: tk-node-in 0.6s ease-out backwards; }
                .tk-node-delay-0 { animation-delay: 0.05s; }
                .tk-node-delay-1 { animation-delay: 0.15s; }
                .tk-node-delay-2 { animation-delay: 0.25s; }
                .tk-node-delay-3 { animation-delay: 0.35s; }
                .tk-node-delay-4 { animation-delay: 0.45s; }
                .tk-node-delay-5 { animation-delay: 0.55s; }
                .tk-node-delay-6 { animation-delay: 0.65s; }
                .tk-node-delay-7 { animation-delay: 0.75s; }
                .tk-node-delay-8 { animation-delay: 0.85s; }
                @keyframes tk-node-in {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
